// src/libs/aiStructuredPrompt.js
// Helper that asks the model to return EXACT JSON matching your schema.
// This file exports `generateStructuredTrip(userPrompt)` which returns the parsed
// JSON object (or throws on failure). It expects you to have a server-side
// endpoint `/api/openai/chat` that proxies requests to OpenAI with your API key.
// at top of src/libs/aiStructuredPrompt.js
import { getAuth } from "firebase/auth";

const API_BASE = (typeof window !== 'undefined' && window._env && window._env.API_BASE)
  || process.env.REACT_APP_API_BASE
  || 'https://api.jift.io';

const SYSTEM_PROMPT = `You are a travel itinerary generator. Output **ONLY** valid JSON that exactly matches this schema. Do not include any prose, Markdown, explanation, or trailing text — only a single JSON object.

Use these exact key names and types. If you don't have a value, use an empty string "" for text, [] for lists, 0 for numbers, and {} for empty objects. Keep arrays even when empty. For itinerary days put at least an empty array item if unknown.

Schema:
{
  "title": "",
  "summary": "",
  "tags": [],
  "trip_days": 0,
  "source": "",
  "budget": {
    "total": "",
    "currency": "",
    "approx_value": 0,
    "notes": ""
  },
  "overview": {
    "destination_info": {
      "description": "",
      "location": "",
      "best_time_to_visit": "",
      "languages": [],
      "local_transport": ""
    },
    "historical_facts": {
      "colonial_past": "",
      "architecture": "",
      "cultural_blend": ""
    },
    "traveller_insights": {
      "local_experience": "",
      "food_tip": "",
      "safety_tip": "",
      "hidden_gems": []
    }
  },
  "highlights": [],
  "logistics": "",
  "packing": [],
  "map": "",
  "itinerary": [
    {
      "title": "",
      "summary": "",
      "activities": [
        {
          "time": "",
          "title": "",
          "location": "",
          "description": "",
          "recommended_places": [],
          "tips": ""
        }
      ]
    }
  ]
}`;

// Helper: extract the first top-level JSON object from a string
// Helper: remove common code fences and leading/trailing noise, then extract first balanced JSON object
function extractFirstJson(text) {
  if (!text || typeof text !== 'string') return null;

  // 1) Remove surrounding markdown code fences like ```json ... ``` or ``` ... ```
  //    and also inline fences like ```json\n{...}\n```
  const fenceRegex = /```(?:json)?\s*([\s\S]*?)\s*```/i;
  const fenceMatch = text.match(fenceRegex);
  if (fenceMatch && fenceMatch[1]) {
    text = fenceMatch[1].trim();
  } else {
    // Remove single backticks surrounding JSON if present: `{"a":1}`
    const inlineFence = text.match(/`({[\s\S]*})`/);
    if (inlineFence && inlineFence[1]) {
      text = inlineFence[1].trim();
    }
  }

  // 2) Now find the first balanced JSON object using a stack (works even if the text contains other braces)
  const startIdx = text.indexOf('{');
  if (startIdx === -1) return null;

  let depth = 0;
  let inString = false;
  let escape = false;
  for (let i = startIdx; i < text.length; i++) {
    const ch = text[i];

    if (inString) {
      if (escape) {
        escape = false;
      } else if (ch === '\\') {
        escape = true;
      } else if (ch === '"') {
        inString = false;
      }
      continue;
    }

    if (ch === '"') {
      inString = true;
      continue;
    }
    if (ch === '{') {
      depth++;
      continue;
    }
    if (ch === '}') {
      depth--;
      if (depth === 0) {
        // found matching close for the first object
        const candidate = text.slice(startIdx, i + 1);
        return candidate;
      }
    }
  }

  // Last resort: attempt to parse a substring starting at first '{' to the last '}' (naive)
  const lastIdx = text.lastIndexOf('}');
  if (lastIdx > startIdx) {
    const cand = text.slice(startIdx, lastIdx + 1);
    return cand;
  }

  // nothing parseable
  return null;
}


// Basic schema sanity-check (top-level only)
function basicSanityCheck(parsed) {
  const required = [
    'title',
    'summary',
    'tags',
    'trip_days',
    'source',
    'budget',
    'overview',
    'itinerary'
  ];

  for (const k of required) {
    if (!(k in parsed)) {
      throw new Error(`Missing required key from model output: ${k}`);
    }
  }
}


// Public: generateStructuredTrip
// userPrompt: string describing destination/requirements, e.g. "3-day Shimla from Delhi in November"
// options: { max_tokens }
export async function generateStructuredTrip(userPrompt, options = {}) {
  if (!userPrompt || typeof userPrompt !== 'string') throw new Error('userPrompt required');

  const messages = [
    { role: 'system', content: SYSTEM_PROMPT },
    { role: 'user', content: `Generate a trip for: "${userPrompt}"\n- Include realistic values where possible.\n- If a field is unknown, leave it as an empty string, empty list, or 0.\n- Keep arrays and nested objects even if empty.` }
  ];

  // call your server-side proxy. Implement /api/openai/chat to forward to OpenAI with your key.
  // 🔐 Attach Firebase ID token
  const auth = getAuth();
  let user = auth.currentUser;

  if (!user) {
    await new Promise(resolve => {
      const unsub = auth.onAuthStateChanged(u => {
        user = u;
        unsub();
        resolve();
      });
    });
  }

  if (!user) {
    throw new Error("Login required");
  }

  const token = await user.getIdToken();

  const res = await fetch(`${API_BASE.replace(/\/+$/, '')}/api/openai/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      model: 'gpt-4o-mini',
      messages,
      temperature: 0,
      max_tokens: options.max_tokens || 1600
    })
  });


  if (!res.ok) {
    let errorData = null;

    try {
      errorData = await res.json();
    } catch {
      const txt = await res.text();
      const err = new Error(txt || "AI request failed");
      err.status = res.status;
      throw err;
    }

    const message =
      errorData?.message ||
      errorData?.error ||
      "AI request failed";

    const err = new Error(message);
    err.code = errorData?.code || null;
    err.status = res.status;

    throw err;
  }

  const data = await res.json();
  const rawText = data?.choices?.[0]?.message?.content ?? data?.output ?? '';
  if (!rawText) throw new Error('Empty response from model');

  // Try to extract JSON robustly
  let jsonText = extractFirstJson(rawText);

  // If still missing, attempt to clean common prefixes/suffixes (e.g., "```json\n" or "result: {...}")
  if (!jsonText) {
    // try to find first '{' and last '}' and hope for the best
    const s = rawText.indexOf('{');
    const e = rawText.lastIndexOf('}');
    if (s !== -1 && e !== -1 && e > s) {
      jsonText = rawText.slice(s, e + 1);
    }
  }

  if (!jsonText) {
    // helpful debug: include a trimmed excerpt of model output in the error — but keep it short
    const excerpt = rawText.slice(0, 800).replace(/\s+/g, ' ');
    throw new Error(`No JSON found in model output (excerpt: ${excerpt})`);
  }

  let parsed;
  try {
    parsed = JSON.parse(jsonText);
  } catch (e) {
    // parsing failed — include excerpt for debugging
    const excerpt = jsonText.slice(0, 1000).replace(/\s+/g, ' ');
    throw new Error(`Model returned invalid JSON: ${e.message}. Extracted JSON excerpt: ${excerpt}`);
  }

  // Basic sanity checks
  basicSanityCheck(parsed);

  // Small normalization fallback: if trip_days is 0 but itinerary array length > 0
  if ((!parsed.trip_days || parsed.trip_days === 0) && Array.isArray(parsed.itinerary) && parsed.itinerary.length) {
    parsed.trip_days = parsed.itinerary.length;
  }

  // Ensure tags is array
  if (!Array.isArray(parsed.tags)) parsed.tags = typeof parsed.tags === 'string' ? parsed.tags.split(/[,#]/).map(s => s.trim()).filter(Boolean) : [];

  return parsed;
}

// Example usage (commented):
// import { generateStructuredTrip } from './aiStructuredPrompt';
// const trip = await generateStructuredTrip('3-day Shimla from Delhi in November');
// console.log(trip.title, trip.trip_days);
